import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import TifpaLogo from '../components/ui/TifpaLogo';
import { Separator } from '@radix-ui/react-separator';

// Define validation schema with Zod
const signInSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(6, { message: 'Password must be at least 6 characters' }),
});

type SignInFormInputs = z.infer<typeof signInSchema>;

export default function Login() {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  // react-hook-form setup with zod resolver
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormInputs>({
    resolver: zodResolver(signInSchema),
  });

  // Redirect if already authenticated
  const from = (location.state as any)?.from?.pathname || '/dashboard';
  if (user) {
    return <Navigate to={from} replace />;
  }

  // Handle form submission
  const onSubmit: SubmitHandler<SignInFormInputs> = async data => {
    setError(null);
    const { error } = await signIn(data.email, data.password);

    if (error) {
      setError(error);
    }
    // If successful, the AuthContext will handle the redirect
  };

  return (
    <div className="h-screen w-screen bg-tifpa-portal">
      <div className="h-full grid md:grid-cols-2 md:grid-rows-1">
        {/* Left Side - Branding */}
        <div className="hidden md:flex flex-col justify-center items-center p-12"></div>

        {/* Right Side - Login Form */}
        <div className="flex flex-col justify-center items-center p-8 md:p-12">
          <div className="w-full max-w-md">
            <div className="md:hidden text-center mb-8"></div>

            <Card className="shadow-xl border-0">
              <CardContent className="p-8 pt-0">
                <div className="text-center pt-4 mb-6">
                  <TifpaLogo className="mx-auto pb-4 w-auto" />
                  <h3 className="text-2xl font-bold text-stone-800">
                    Team Portal
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Streamline your workflow. Access your assignments, track
                    progress, and collaborate with your team.
                  </p>
                </div>

                {error && (
                  <Alert className="mb-6 border-red-200 bg-red-50">
                    <AlertDescription className="text-red-800">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <Label
                      htmlFor="email"
                      className="text-stone-700 font-medium"
                    >
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      className="mt-1"
                      placeholder="Enter your email"
                      autoComplete="email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="password"
                      className="text-stone-700 font-medium"
                    >
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      {...register('password')}
                      className="mt-1"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary hover:bg-tifpa-rose/30 text-white font-medium py-2.5"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm text-stone-600">
                  <p>Need help? Contact your administrator</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
